import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Heading, Box, Text, Button, SimpleGrid, Image, Icon, Link as ChakraLink, Spinner, Center, Table, Thead, Tr, Th, Tbody, Td, Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow, PopoverCloseButton, PopoverBody, PopoverFooter, ButtonGroup, Tooltip } from '@chakra-ui/react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import type { ChartOptions } from 'chart.js';
import { supabase } from '../supabaseClient';
import { RaceStats } from '../types/types';
import { TriangleDownIcon, TriangleUpIcon, InfoIcon } from '@chakra-ui/icons';
import { timeToSeconds } from '../utils/timeUtils';
import teamsConfig from '../config/teams.json';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import { TableHeader } from './shared/TableHeader';
import BackButton from './shared/BackButton';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

interface TeamSummary {
  team: string;
  memberCount: number;
  positionCounts: Record<string, number>;
  members: string[];
}

// Add interface for intermediate team data
interface TeamData {
  team: string;
  members: Set<string>;
  positionCounts: Record<string, number>;
}

interface RaceData {
  race_date: string;
  race_description: string;
  race_location: string;
}

interface RaceWithDetails extends RaceStats {
  race_data: RaceData;
  name: string;
  position: string;
  total_time: string;
  first_run: string;
  second_run: string;
  race_category: string;
  'race_data.race_date': string;
  'race_data.race_description': string;
  'race_data.race_location': string;
}

// Update SortConfig type to handle nested properties
type SortableKeys = keyof RaceWithDetails | 'race_data.race_date' | 'race_data.race_description' | 'race_data.race_location';

interface SortConfig {
  key: SortableKeys | null;
  direction: 'asc' | 'desc' | null;
}

interface TeamConfig {
  logo?: string;
  website?: string;
}

// Move getTeamConfig outside of components to make it globally available
const getTeamConfig = (teamName: string): TeamConfig => {
  const configEntry = Object.entries(teamsConfig).find(
    ([key]) => key.toLowerCase() === teamName?.toLowerCase()
  );
  return configEntry ? configEntry[1] : {};
};

// Add this near the top of the file with other shared configurations
const chartOptions: ChartOptions<'bar'> = {
  responsive: true,
  plugins: {
    legend: {
      position: 'top' as const,
    },
    title: {
      display: true,
      text: 'Position Distribution'
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1
      }
    }
  }
};

// Add normalizeTime helper function
const normalizeTime = (timeStr: string): number => {
  if (!timeStr) return 999999;
  
  // Handle MM:SS.ms format (1:00.20)
  if (timeStr.includes(':') && timeStr.split(':').length === 2) {
    const [minutes, secondsPart] = timeStr.split(':');
    return (parseInt(minutes) * 60) + parseFloat(secondsPart);
  }
  
  // Handle HH:MM:SS.ms format (0:00:34.28)
  if (timeStr.includes(':') && timeStr.split(':').length === 3) {
    const [hours, minutes, secondsPart] = timeStr.split(':');
    return (parseInt(hours) * 3600) + (parseInt(minutes) * 60) + parseFloat(secondsPart);
  }
  
  // Handle SS.ms format (50.95)
  return parseFloat(timeStr);
};

// Calculate team win rate based on skiing-style points per unique team member.
// Points for positions 1–6: 100, 80, 60, 50, 45, 40.
const calculateTeamWinRate = (
  positionCounts: Record<string, number>,
  memberCount: number
): string => {
  const POINTS_PER_POSITION: Record<string, number> = {
    '1': 100,
    '2': 80,
    '3': 60,
    '4': 50,
    '5': 45,
    '6': 40,
  };

  if (!memberCount || memberCount <= 0) {
    return '0.0';
  }

  const totalPoints =
    (positionCounts['1'] || 0) * POINTS_PER_POSITION['1'] +
    (positionCounts['2'] || 0) * POINTS_PER_POSITION['2'] +
    (positionCounts['3'] || 0) * POINTS_PER_POSITION['3'] +
    (positionCounts['4'] || 0) * POINTS_PER_POSITION['4'] +
    (positionCounts['5'] || 0) * POINTS_PER_POSITION['5'] +
    (positionCounts['6'] || 0) * POINTS_PER_POSITION['6'];

  const winRate = totalPoints / memberCount;

  return winRate.toFixed(1);
};

// Calculate cumulative points earned by the team from positions 1–6.
const calculateTeamPoints = (positionCounts: Record<string, number>): number => {
  const POINTS_PER_POSITION: Record<string, number> = {
    '1': 100,
    '2': 80,
    '3': 60,
    '4': 50,
    '5': 45,
    '6': 40,
  };

  return (
    (positionCounts['1'] || 0) * POINTS_PER_POSITION['1'] +
    (positionCounts['2'] || 0) * POINTS_PER_POSITION['2'] +
    (positionCounts['3'] || 0) * POINTS_PER_POSITION['3'] +
    (positionCounts['4'] || 0) * POINTS_PER_POSITION['4'] +
    (positionCounts['5'] || 0) * POINTS_PER_POSITION['5'] +
    (positionCounts['6'] || 0) * POINTS_PER_POSITION['6']
  );
};

const TeamDetail = () => {
  const { team } = useParams();
  
  if (!team) {
    return (
      <Container maxW="container.xl" py={8}>
        <BackButton to="/" label="Back to Dashboard" />
        <Heading mb={8}>No team specified</Heading>
      </Container>
    );
  }

  return <SingleTeamView team={team} />;
};

const SingleTeamView = ({ team }: { team: string }) => {
  const navigate = useNavigate();
  const [teamStats, setTeamStats] = useState<TeamSummary[]>([]);
  const [races, setRaces] = useState<RaceWithDetails[]>([]);
  const [teamMembers, setTeamMembers] = useState<RacerSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ 
    key: null,
    direction: null
  });
  // Track logo load failures so we show "No logo available" instead of broken image
  const [logoError, setLogoError] = useState(false);

  // Add RacerSummary interface similar to Racers component
  interface RacerSummary {
    name: string;
    team: string | null;
    best_position: number;
    race_category: string;
    year: number | null;
    best_time: string | null;
    last_race: string | null;
    num_races: number;
  }

  useEffect(() => {
    const fetchTeamData = async () => {
      setIsLoading(true);
      try {
        // First fetch team stats (keep existing code)
        const { data: statsData, error: statsError } = await supabase
          .from('medvedica_race_stats')
          .select('*')
          .not('team', 'is', null)
          .eq('team', team);

        if (statsError) {
          console.error('Error fetching team data:', statsError);
          return;
        }

        // Ensure we are working with a concrete array of race stats
        const typedStatsData = (statsData ?? []) as RaceStats[];

        // Process team stats (keep existing code for positions)
        const positions = typedStatsData.reduce((acc, race) => {
          acc[race.position] = (acc[race.position] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        // Fetch team members from same source as Teams overview: gold_team = team (no duplicities)
        const { data: racersData, error: racersError } = await supabase
          .from('frontend_racers_summary_mv')
          .select('*')
          .eq('gold_team', team)
          .order('gold_name');

        if (racersError) {
          console.error('Error fetching team members:', racersError);

          // Fallback: derive members directly from race stats if summary view fails
          const fallbackMembers = Array.from(
            new Set(typedStatsData.map(race => race.name))
          ).sort((a, b) => a.localeCompare(b));

          setTeamStats([{
            team,
            memberCount: fallbackMembers.length,
            positionCounts: positions,
            members: fallbackMembers
          }]);
        } else if (racersData) {
          const racerSummaries = racersData.map((record: any) => ({
            name: record.gold_name,
            team: record.gold_team,
            race_category: record.gold_country || '',
            best_position: record.best_position,
            year: record.gold_year ? parseInt(record.gold_year) : null,
            best_time: record.best_time,
            last_race: record.last_race,
            num_races: record.num_races
          }));
          
          setTeamMembers(racerSummaries);

          // Use the same unique-member computation as in Teams overview:
          // derive members from the golden-record racers summary (frontend_racers_summary_mv) so counts match.
          const uniqueMembersFromSummary: string[] = Array.from(
            new Set<string>(racersData.map((record: any) => record.gold_name as string))
          ).sort((a, b) => a.localeCompare(b));

          setTeamStats([{
            team,
            memberCount: uniqueMembersFromSummary.length,
            positionCounts: positions,
            members: uniqueMembersFromSummary
          }]);
        }

        // Fetch race history with race_data join (keep existing code)
        const { data: raceData, error: raceError } = await supabase
          .from('medvedica_race_stats')
          .select(`
            *,
            race_data:medvedica_race_data(
              race_description,
              race_date,
              race_location
            )
          `)
          .eq('team', team);

        if (raceError) {
          console.error('Error fetching race history:', raceError);
        } else if (raceData) {
          setRaces(raceData);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTeamData();
  }, [team]);

  // Reset logo error when team changes so we don't carry over failed state
  useEffect(() => {
    setLogoError(false);
  }, [team]);

  const handleSort = (key: SortableKeys) => {
    setSortConfig(current => {
      if (current.key !== key) {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      if (current.direction === 'desc') {
        // Return to default sort instead of null
        return { key: 'race_data.race_date', direction: 'desc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const sortData = (data: RaceWithDetails[]): RaceWithDetails[] => {
    // Default sort by date (newest first), then category, then position
    const defaultSort = (a: RaceWithDetails, b: RaceWithDetails) => {
      // Primary sort by date (newest first)
      const dateA = a.race_data?.race_date || '';
      const dateB = b.race_data?.race_date || '';
      const dateCompare = dateB.localeCompare(dateA); // desc
      if (dateCompare !== 0) return dateCompare;
      
      // Secondary sort by category
      const categoryA = a.race_category || '';
      const categoryB = b.race_category || '';
      const categoryCompare = categoryA.localeCompare(categoryB); // asc
      if (categoryCompare !== 0) return categoryCompare;
      
      // Tertiary sort by position (numbers first, then strings, then nulls)
      const posA = a.position;
      const posB = b.position;

      // Handle null/undefined/empty values - put last
      if (!posA && !posB) return 0;
      if (!posA) return 1;
      if (!posB) return -1;

      // Handle "0" values - put last
      if (posA === "0" && posB === "0") return 0;
      if (posA === "0") return 1;
      if (posB === "0") return -1;

      // Parse numbers
      const numA = parseInt(posA);
      const numB = parseInt(posB);

      // Handle string values (like "DNF") - put last
      if (isNaN(numA) && isNaN(numB)) return posA.localeCompare(posB);
      if (isNaN(numA)) return 1;
      if (isNaN(numB)) return -1;

      // Compare valid numbers
      return numA - numB;
    };

    if (!sortConfig.key || !sortConfig.direction) {
      return [...data].sort(defaultSort);
    }

    return [...data].sort((a, b) => {
      const key = sortConfig.key!;
      const direction = sortConfig.direction!;

      // Handle position column specially
      if (key === 'position') {
        const posA = a.position;
        const posB = b.position;

        // Handle null/undefined/empty values - put last
        if (!posA && !posB) return 0;
        if (!posA) return direction === 'asc' ? 1 : -1;
        if (!posB) return direction === 'asc' ? -1 : 1;

        // Handle "0" values - put last
        if (posA === "0" && posB === "0") return 0;
        if (posA === "0") return direction === 'asc' ? 1 : -1;
        if (posB === "0") return direction === 'asc' ? -1 : 1;

        // Parse numbers
        const numA = parseInt(posA);
        const numB = parseInt(posB);

        // Handle string values (like "DNF") - put last
        if (isNaN(numA) && isNaN(numB)) return direction === 'asc' ? 
          posA.localeCompare(posB) : 
          posB.localeCompare(posA);
        if (isNaN(numA)) return 1;
        if (isNaN(numB)) return -1;

        // Compare valid numbers
        return direction === 'asc' ? numA - numB : numB - numA;
      }

      // Rest of the sorting logic remains the same
      const aValue = a[key];
      const bValue = b[key];
      
      if (aValue === null || aValue === undefined) {
        return direction === 'asc' ? 1 : -1;
      }
      if (bValue === null || bValue === undefined) {
        return direction === 'desc' ? 1 : -1;
      }

      if (['total_time', 'first_run', 'second_run'].includes(key)) {
        const aTime = normalizeTime(String(aValue));
        const bTime = normalizeTime(String(bValue));
        return direction === 'asc' ? aTime - bTime : bTime - aTime;
      }

      if (key === 'race_data.race_date') {
        const dateA = a.race_data?.race_date || '';
        const dateB = b.race_data?.race_date || '';
        return direction === 'asc' 
          ? dateA.localeCompare(dateB) 
          : dateB.localeCompare(dateA);
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return direction === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  };

  if (isLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <BackButton to="/" label="Back to Dashboard" />
        <Center h="50vh">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            size="xl"
          />
        </Center>
      </Container>
    );
  }

  if (teamStats.length === 0) {
    return (
      <Container maxW="container.xl" py={8}>
        <BackButton to="/" label="Back to Dashboard" />
        <Heading mb={8}>No teams found</Heading>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <BackButton to="/team" label="Back to Teams" />
      
      <Box px={4} pt={2}>
        <Heading mb={8}>Team Statistics: {team}</Heading>

        {/* Team Information Box */}
        <Box p={5} shadow="md" borderWidth="1px" borderRadius="md" mb={8}>
          <SimpleGrid columns={[1, 2]} gap={6}>
            <Box>
              <Heading size="lg" mb={4}>{team}</Heading>
              <Text mb={2}><strong>Total Members Racing:</strong> {teamStats[0].memberCount}</Text>
              <Text mb={2}><strong>Total Races:</strong> {
                Object.values(teamStats[0].positionCounts).reduce((a, b) => a + b, 0)
              }</Text>
              {getTeamConfig(team)?.website && (
                <Text mb={2}>
                  <strong>Website:</strong>{' '}
                  <Text
                    as="a"
                    href={getTeamConfig(team).website}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="blue.500"
                    _hover={{ textDecoration: 'underline' }}
                    /* Allow long URLs to break on mobile */
                    wordBreak="break-all"
                  >
                    {getTeamConfig(team).website}
                  </Text>
                </Text>
              )}
              
              {/* Team Zone Popover */}
              <Popover placement='right' closeOnBlur={false}>
                <PopoverTrigger>
                  <Button 
                    size="sm" 
                    leftIcon={<InfoIcon />}
                    bg="#2c58dc"  // Matches sidebar blue
                    color="white"
                    _hover={{ bg: '#3b3dcd' }}  // Slightly darker on hover
                    mt={2}
                    mb={4}
                  >
                    Team Zone
                  </Button>
                </PopoverTrigger>
                <PopoverContent color='white' bg='#6694ec' borderColor='#2C5282'>
                  <PopoverHeader pt={4} fontWeight='bold' border='0'>
                    Team Zone Information
                  </PopoverHeader>
                  <PopoverArrow bg='blue.800' />
                  <PopoverCloseButton />
                  <PopoverBody>
                    <Text mb={2}>
                      <strong>Active Members:</strong> {teamStats[0].members.length}
                    </Text>
                    <Text mb={2}>
                      <strong>Top 3 Finishes:</strong> {
                        (teamStats[0].positionCounts['1'] || 0) +
                        (teamStats[0].positionCounts['2'] || 0) +
                        (teamStats[0].positionCounts['3'] || 0)
                      }
                    </Text>
                    <Text mb={2}>
                      <strong>Points earned:</strong>{' '}
                      {calculateTeamPoints(teamStats[0].positionCounts)}
                    </Text>
                    <Tooltip
                      label={(
                        <Box fontSize="xs" fontWeight="normal">
                          Rate = Members / body za výhry.
                          <br />
                          1. miesto – 100 bodov, 2. miesto – 80 bodov, 3. miesto – 60 bodov, 4. miesto – 50 bodov, 5. miesto – 45 bodov, 6. miesto – 40 bodov.
                        </Box>
                      )}
                      hasArrow
                      placement="top"
                    >
                      <Text>
                        <strong>Win Rate:</strong>{' '}
                        {
                          calculateTeamWinRate(
                            teamStats[0].positionCounts,
                            teamStats[0].memberCount
                          )
                        }%
                      </Text>
                    </Tooltip>
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            </Box>
            <Box display="flex" justifyContent="center" alignItems="center">
              {getTeamConfig(team)?.logo && !logoError ? (
                <Image
                  src={getTeamConfig(team).logo}
                  alt={`${team} logo`}
                  maxH="150px"
                  objectFit="contain"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <Box 
                  bg="gray.100" 
                  h="150px" 
                  w="100%" 
                  borderRadius="md" 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="center"
                >
                  <Text color="gray.500">No logo available</Text>
                </Box>
              )}
            </Box>
          </SimpleGrid>
        </Box>

        {/* Team Stats Cards */}
        {teamStats.map((teamData) => (
          <Box 
            key={teamData.team}
            p={5} 
            shadow="md" 
            borderWidth="1px" 
            borderRadius="md"
            mb={6}
            cursor={team ? 'default' : 'pointer'}
            onClick={() => !team && navigate(`/team/${encodeURIComponent(teamData.team)}`)}
            _hover={!team ? { bg: 'gray.50' } : undefined}
          >
            <SimpleGrid columns={[1, 2]} gap={6}>
              <Box>
                <Heading size="md" mb={4}>{teamData.team}</Heading>
                {/* Position Distribution Chart */}
                <Bar 
                  data={{
                    labels: ['1st', '2nd', '3rd', '4th', '5th', '6th'],
                    datasets: [{
                      label: 'Positions Distribution',
                      data: [1,2,3,4,5,6].map(pos => teamData.positionCounts[pos] || 0),
                      backgroundColor: [
                        '#FFD700', // Gold
                        '#C0C0C0', // Silver
                        '#CD7F32', // Bronze
                        '#A0A0A0',
                        '#808080',
                        '#606060',
                      ],
                    }],
                  }} 
                  // Preserve interactive chart behaviour on desktop, but disable
                  // Chart.js pointer events on coarse (touch) devices to avoid
                  // rare null getDatasetMeta() errors when tapping on mobile.
                  options={
                    (typeof window !== 'undefined' &&
                      window.matchMedia &&
                      window.matchMedia('(pointer: coarse)').matches)
                      ? { ...(chartOptions as any), events: [] }
                      : chartOptions
                  } 
                />
              </Box>

              {/* Team Members List - Updated */}
              <Box>
                <Heading size="md" mb={4}>Team Members</Heading>
                <Box maxH="400px" overflowY="auto">
                  {teamMembers.map(member => (
                    <Box 
                      key={member.name} 
                      as={RouterLink} 
                      to={`/racer/${encodeURIComponent(member.name)}`}
                      state={{ from: 'teamDetail', teamName: team }}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={2}
                      p={2}
                      borderRadius="md"
                      _hover={{ bg: 'gray.50', color: 'blue.600' }}
                    >
                      <Text fontWeight="medium">{member.name}</Text>
                      <Text fontSize="sm" color="gray.600">
                        {member.race_category && `${member.race_category} • `}
                        {member.year && `${member.year} • `}
                        {member.best_position === null ? 'n/a' : 
                          member.best_position === 0 ? 'DNF' : `Best: ${member.best_position}`} • 
                        {`Races: ${member.num_races}`}
                      </Text>
                    </Box>
                  ))}
                </Box>
              </Box>
            </SimpleGrid>
          </Box>
        ))}

        {/* Race History Section - Added debug info */}
        <Box mt={8}>
          <Heading size="md" mb={4}>Race History</Heading>
          {races.length === 0 ? (
            <Text>No race history available</Text>
          ) : (
            <Box overflowX="auto" shadow="md" borderRadius="lg">
              <Table variant="simple" size={{ base: 'sm', md: 'md' }}>
                <Thead bg="gray.50">
                  <Tr>
                    <TableHeader<RaceWithDetails>
                      label="Date"
                      sortKey={'race_data.race_date' as SortableKeys}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                    />
                    <TableHeader<RaceWithDetails>
                      label="Race"
                      sortKey={'race_data.race_description' as SortableKeys}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                    />
                    <TableHeader<RaceWithDetails>
                      label="Category"
                      sortKey={'race_category' as SortableKeys}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                    />
                    <TableHeader<RaceWithDetails>
                      label="Location"
                      sortKey={'race_data.race_location' as SortableKeys}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                    />
                    <TableHeader<RaceWithDetails>
                      label="Racer"
                      sortKey={'name' as SortableKeys}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                    />
                    <TableHeader<RaceWithDetails>
                      label="Position"
                      sortKey={'position' as SortableKeys}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                    />
                    <TableHeader<RaceWithDetails>
                      label="Total Time"
                      sortKey={'total_time' as SortableKeys}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                    />
                    <TableHeader<RaceWithDetails>
                      label="First Run"
                      sortKey={'first_run' as SortableKeys}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                    />
                    <TableHeader<RaceWithDetails>
                      label="Second Run"
                      sortKey={'second_run' as SortableKeys}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                    />
                  </Tr>
                </Thead>
                <Tbody>
                  {sortData(races).map((race: RaceWithDetails, index: number) => (
                    <Tr 
                      key={race.id}
                      style={{ 
                        backgroundColor: index % 2 === 0 ? 'white' : '#f7fafc',
                        cursor: 'pointer'
                      }}
                      onClick={() => navigate(`/race/${encodeURIComponent(race.race_code)}`)}
                    >
                      <Td whiteSpace="nowrap">{race.race_data?.race_date ? new Date(race.race_data.race_date).toLocaleDateString() : '-'}</Td>
                      {/* Race description – truncated on mobile */}
                      <Td maxW="140px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={race.race_data?.race_description || ''}>
                        {race.race_data?.race_description || '-'}
                      </Td>
                      <Td whiteSpace="nowrap">{race.race_category || '-'}</Td>
                      {/* Location – truncated on mobile */}
                      <Td maxW="100px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={race.race_data?.race_location || ''}>
                        {race.race_data?.race_location || '-'}
                      </Td>
                      <Td whiteSpace="nowrap">{race.name}</Td>
                      <Td>{race.position || ""}</Td>
                      <Td isNumeric>{race.total_time || '-'}</Td>
                      <Td isNumeric>{race.first_run || '-'}</Td>
                      <Td isNumeric>{race.second_run || '-'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default TeamDetail; 